import { useEffect, useRef } from 'react';
import type {
  RealtimeChannel,
  RealtimePostgresChangesFilter,
  RealtimePostgresChangesPayload,
} from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { ensureRealtimeAuth } from '@/integrations/supabase/realtimeAuth';
import { useAuth } from '@/contexts/AuthContext';

// The ONLY place in this app that may call supabase.channel().
//
// Two production bugs are fixed here at once; both were invisible because the server
// reports success in either case.
//
// (1) Anon joins. subscribe() reads socket.accessTokenValue synchronously, so a
//     channel created before the token is in place joins as anon and — with our
//     auth.uid() = user_id policies — receives nothing, forever. Full mechanism in
//     integrations/supabase/realtimeAuth.ts. Hence: await ensureRealtimeAuth() first,
//     and do not subscribe at all when there is no session.
//
// (2) A shared topic makes the channel destroy itself. This one is counter-intuitive,
//     so the exact mechanism, verified by running the real RealtimeClient against a stub
//     transport, not inferred:
//
//     RealtimeClient.channel(topic) does NOT create a channel per call. It looks the
//     topic up and returns the EXISTING one:
//
//       channel(topic, params) {
//         const realtimeTopic = `realtime:${topic}`;
//         const exists = this.getChannels().find((c) => c.topic === realtimeTopic);
//         if (!exists) { ...push new...; return chan; } else { return exists; }
//       }
//
//     and subscribe() early-returns unless `this.state == CHANNEL_STATES.closed`.
//     So five mounts of useMcAccessStatus (AccessRevokedBanner, AccessRevokedOverlay,
//     WorkbenchUploadAction, useOptimizationJob, useUploadJob — all live at once) sharing the
//     topic `mc-access-<id>` produced: ONE channel, FIVE accumulated postgres_changes
//     bindings, but only ONE filter in the join payload, because the join was built and
//     sent when the first mount subscribed and the later subscribe() calls did nothing.
//
//     The join-ok handler then reconciles client bindings against server filters
//     index-by-index, and on the first index the server does not have:
//
//       const serverPostgresFilter = postgres_changes && postgres_changes[i];
//       if (serverPostgresFilter && ...matches...) { ...push... }
//       else { this.unsubscribe(); this.state = errored; callback(CHANNEL_ERROR, ...); }
//
//     (RealtimeChannel.js) i=1 hits `undefined` and the channel tears itself down. THAT
//     is the join -> phx_leave -> phx_close seen in the production frame capture, not an
//     eviction. _leaveOpenTopic() never runs for this at all, because channel() never
//     creates a second channel with the same topic in the first place.
//
//     Two more consequences of the shared topic, same root: RealtimeClient._remove()
//     filters by topic, so unmounting ONE of the five removed the channel for ALL of
//     them; and only the first mount's handler was ever wired to a live server filter.
//
//     Giving every subscription instance its own topic makes each mount a real channel
//     with exactly one binding and one matching server filter, so the reconciliation
//     always succeeds.
//
//     DO NOT "simplify" this back to a shared topic string. It looks redundant and is
//     not: it is the thing that keeps the binding count and the join payload in sync.
//
//     Cost, accepted deliberately: N mounts of the same hook now mean N server-side
//     subscriptions instead of one self-destructing channel. The duplicated topics are
//     the low-volume ones (merchant_centers ~8k lifetime updates, subscriptions 13);
//     the high-volume tables have exactly one consumer each, so nothing multiplies
//     there. Reducing useMcAccessStatus to a single mount would be better still and is
//     a separate change.

export interface PostgresChangesBinding {
  event: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
  /** Defaults to 'public'. */
  schema?: string;
  table: string;
  /**
   * e.g. `user_id=eq.<uuid>`.
   *
   * Careful with DELETE. The filter is matched against the OLD record, and under
   * REPLICA IDENTITY DEFAULT that record carries only the primary key. walrus is
   * fail-closed there (a filter naming an absent column evaluates to false), so an
   * `event: '*'` or `event: 'DELETE'` subscription filtered on a non-key column such as
   * `user_id` or `feed_id` receives no DELETE events at all, silently. Our '*'
   * subscriptions accept that: they only invalidate a query key, and job rows are not
   * deleted in normal operation. If a DELETE ever has to be observed, the table needs
   * REPLICA IDENTITY FULL. (The Supabase docs still say DELETE cannot be filtered at
   * all; that predates walrus migration 0011 and no longer matches deployed behaviour.)
   */
  filter?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PostgresChangesHandler = (payload: RealtimePostgresChangesPayload<any>) => void;

// Per-module-instance prefix, not just a counter. Under Vite HMR this module can be
// re-evaluated while channels created by the previous instance are still on the socket.
// A bare counter restarts at 0, the regenerated `topic#1` collides with the live one,
// channel() hands back the existing channel, subscribe() early-returns because it is
// already joined, and the fresh binding is never registered server-side — a dev-only
// ghost where the handler simply never fires until a full reload.
const TOPIC_INSTANCE = Math.random().toString(36).slice(2, 8);
let topicSequence = 0;

/**
 * Subscribes to postgres_changes on a channel that is guaranteed to be authorized as
 * the logged-in user, and tears it down on unmount.
 *
 * Pass `topic = null` (or a null binding) to disable the subscription.
 *
 * The handler is held in a ref, and that ref is LOAD-BEARING — not a convenience.
 * The effect deps are the identity of the subscription itself (topic, user, event,
 * schema, table, filter). For useAsyncJob none of those is feed-scoped: `channelName`
 * is a constant per call site and the filter is `user_id=eq.<uid>`, so switching feeds
 * changes no dependency and the channel is deliberately NOT rebuilt. The ref is
 * therefore the only thing that keeps the handler pointing at the current feed's
 * queryKey. Bind the raw `onChange` instead of `onChangeRef.current` and job progress
 * silently stops updating after a feed switch. There is a test pinning exactly this;
 * it asserts on function identity, because a test whose callback closes over a mutable
 * variable passes either way.
 */
export function useRealtimePostgresChanges(
  topic: string | null,
  binding: PostgresChangesBinding | null,
  onChange: PostgresChangesHandler,
): void {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const event = binding?.event ?? null;
  const schema = binding?.schema ?? 'public';
  const table = binding?.table ?? null;
  const filter = binding?.filter ?? null;

  useEffect(() => {
    // No session means no authorized subscription is possible. Bailing out here is not
    // a silent drop: userId is a dependency, so the effect re-runs and subscribes as
    // soon as auth lands. Every caller inherits that for free, including the ones that
    // do not otherwise depend on the user (useMcAccessStatus, OptimizationScoreCard,
    // AdvancedModePanel).
    if (!topic || !userId || !event || !table) return;

    let cancelled = false;
    let channel: RealtimeChannel | null = null;

    void (async () => {
      let authorized = false;
      try {
        // Bound to this effect's userId: a probe still in flight for the previous user
        // must not authorize this channel, or it joins with the wrong JWT and is just
        // as silently empty as an anon join.
        authorized = await ensureRealtimeAuth(userId);
      } catch (err) {
        // A failing session probe must not surface as an unhandled rejection. Losing a
        // subscription degrades to the polling that most of these screens already do;
        // an unhandled rejection would be a hard error in the console for a soft
        // failure. The effect re-runs on the next auth change.
        console.error('useRealtimePostgresChanges: realtime auth failed', err);
        return;
      }
      // The effect may have been cleaned up while we awaited the session.
      if (cancelled || !authorized) return;

      // realtime-js declares one `on('postgres_changes', ...)` overload per event
      // LITERAL ('*' / 'INSERT' / 'UPDATE' / 'DELETE'), so a variable holding the union
      // of those literals matches none of them and TS falls through to the 'system'
      // overload. The '*' overload is the widest one and its payload type
      // (RealtimePostgresChangesPayload) is exactly what PostgresChangesHandler takes,
      // so we type the filter as that. Runtime is untouched — `event` still carries the
      // caller's real event string.
      const changesFilter = {
        event,
        schema,
        table,
        ...(filter ? { filter } : {}),
      } as RealtimePostgresChangesFilter<'*'>;

      topicSequence += 1;
      // Assigned before subscribe(): everything from here to the end of this block is
      // synchronous, so `cancelled` cannot flip in between and the cleanup below always
      // sees the channel. (An earlier version re-checked `cancelled` after subscribe();
      // that branch was unreachable.)
      channel = supabase
        .channel(`${topic}#${TOPIC_INSTANCE}${topicSequence}`)
        .on('postgres_changes', changesFilter, (payload) => onChangeRef.current(payload))
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [topic, userId, event, schema, table, filter]);
}
